import { useCallback, useEffect, useRef, type RefObject } from "react";
import {
  lockViewportHeight,
  setViewportFillSuppressed,
  unlockViewportHeight,
} from "../../utils/viewportFill";

const HTML_KEYBOARD_CLASS = "lf-h5-cs-keyboard-open";
const MESSAGE_CHAT_SHELL_SELECTOR = ".space-message-chat-shell";
const BLUR_DEFER_MS = 200;
const CLOSE_SETTLE_MS = 280;
/** 键盘动画中途少改几何；收尾再对齐一次 */
const OPEN_SYNC_DELAYS_MS = [0, 280, 480] as const;
const HEIGHT_CHANGE_EPSILON_PX = 1;

function recoverViewportAfterKeyboard(): void {
  window.scrollTo(0, 0);
  const main = document.querySelector<HTMLElement>(".lf-h5-space-main");
  if (main) {
    main.scrollTop = 0;
  }
}

function scheduleViewportRecovery(): void {
  recoverViewportAfterKeyboard();
  window.requestAnimationFrame(recoverViewportAfterKeyboard);
  window.setTimeout(recoverViewportAfterKeyboard, 100);
}

function clearShellViewportStyles(el: HTMLElement | null | undefined): void {
  if (!el) return;
  el.style.removeProperty("top");
  el.style.removeProperty("height");
  el.style.removeProperty("bottom");
}

/**
 * iOS WKWebView：祖先高度变化后原生光标常停在旧 Y。
 * 不 blur，仅重刷 selection / 轻微 transform 强制重绘。
 */
function nudgeFocusedTextCaret(): void {
  const el = document.activeElement;
  if (!(el instanceof HTMLTextAreaElement) && !(el instanceof HTMLInputElement)) {
    return;
  }
  if (el !== document.activeElement) return;
  const start = el.selectionStart ?? 0;
  const end = el.selectionEnd ?? 0;
  const prevTransform = el.style.transform;
  el.style.transform = "translateZ(0)";
  window.requestAnimationFrame(() => {
    if (document.activeElement !== el) {
      el.style.transform = prevTransform;
      return;
    }
    el.style.transform = prevTransform;
    try {
      el.setSelectionRange(start, end);
    } catch {
      /* ignore */
    }
  });
}

/**
 * Tab 进入：壳本身是 fixed，直接对齐 visualViewport。
 * 消息中心进入：内层壳是 relative（嵌在 .space-message-chat-shell 里），
 * 必须改外层 fixed 壳，否则 height 按视口写到 relative 上会把输入区顶到键盘下。
 */
function resolveKeyboardShell(shellEl: HTMLElement | null): HTMLElement | null {
  if (!shellEl) return null;
  const outer = shellEl.closest(MESSAGE_CHAT_SHELL_SELECTOR);
  return (outer as HTMLElement | null) ?? shellEl;
}

/**
 * 键盘弹起：壳层高度对齐 visualViewport。
 * 注意：聚焦期间绝不能改 fixed 壳的 top（如 vv.offsetTop）。
 * iOS WKWebView 会在祖先几何变化后把原生光标画在旧坐标上（输入框位置对、光标飘到聊天区）。
 *
 * height 用 vv.height + vv.offsetTop：顶边钉 0 时，底边对齐可视区底，
 * 避免 offsetTop>0 时输入框与键盘之间出现空档（又不用 top=offsetTop 追视口）。
 */
export function useCsKeyboardLayout(shellRef: RefObject<HTMLElement | null>) {
  const inputFocusedRef = useRef(false);
  const blurDeferTimerRef = useRef<number | null>(null);
  const closeSettleTimerRef = useRef<number | null>(null);
  const openSyncTimersRef = useRef<number[]>([]);
  const lastAppliedHeightRef = useRef<number | null>(null);
  const resizeDebounceTimerRef = useRef<number | null>(null);

  const setKeyboardOpen = useCallback((open: boolean) => {
    document.documentElement.classList.toggle(HTML_KEYBOARD_CLASS, open);
  }, []);

  const resetShellViewport = useCallback(() => {
    const el = shellRef.current;
    if (!el) return;
    // 同时清内外壳，避免上次错误写在内层 relative 上的残留
    clearShellViewportStyles(el);
    clearShellViewportStyles(el.closest(MESSAGE_CHAT_SHELL_SELECTOR) as HTMLElement | null);
    lastAppliedHeightRef.current = null;
  }, [shellRef]);

  const syncShellToVisualViewport = useCallback(
    (options?: { recoverScroll?: boolean; nudgeCaret?: boolean }) => {
      const target = resolveKeyboardShell(shellRef.current);
      const vv = window.visualViewport;
      if (!target || !vv || !inputFocusedRef.current) {
        return;
      }
      // 消息中心路径：不要再给内层 relative 壳写 top/height
      if (target !== shellRef.current) {
        clearShellViewportStyles(shellRef.current);
      }

      if (options?.recoverScroll) {
        recoverViewportAfterKeyboard();
      }

      // 顶边钉 0；高度覆盖到可视区底边（含 offsetTop），消除键盘上方空档
      const nextHeight = Math.round(vv.height + vv.offsetTop);
      if (
        lastAppliedHeightRef.current !== null &&
        Math.abs(lastAppliedHeightRef.current - nextHeight) < HEIGHT_CHANGE_EPSILON_PX
      ) {
        return;
      }
      lastAppliedHeightRef.current = nextHeight;
      if (target.style.top !== "0px") {
        target.style.top = "0px";
      }
      target.style.height = `${nextHeight}px`;
      target.style.bottom = "auto";

      if (options?.nudgeCaret) {
        nudgeFocusedTextCaret();
      }
    },
    [shellRef]
  );

  const clearOpenSyncTimers = useCallback(() => {
    openSyncTimersRef.current.forEach((id) => window.clearTimeout(id));
    openSyncTimersRef.current = [];
    if (resizeDebounceTimerRef.current !== null) {
      window.clearTimeout(resizeDebounceTimerRef.current);
      resizeDebounceTimerRef.current = null;
    }
  }, []);

  const scheduleOpenSyncBurst = useCallback(() => {
    clearOpenSyncTimers();
    syncShellToVisualViewport({ recoverScroll: true, nudgeCaret: false });
    requestAnimationFrame(() => {
      syncShellToVisualViewport({ recoverScroll: false, nudgeCaret: false });
    });
    OPEN_SYNC_DELAYS_MS.forEach((delay, index) => {
      const id = window.setTimeout(() => {
        const isLast = index === OPEN_SYNC_DELAYS_MS.length - 1;
        syncShellToVisualViewport({
          recoverScroll: false,
          nudgeCaret: isLast,
        });
      }, delay);
      openSyncTimersRef.current.push(id);
    });
  }, [clearOpenSyncTimers, syncShellToVisualViewport]);

  const openKeyboardLayout = useCallback(() => {
    if (closeSettleTimerRef.current !== null) {
      window.clearTimeout(closeSettleTimerRef.current);
      closeSettleTimerRef.current = null;
    }
    inputFocusedRef.current = true;
    setViewportFillSuppressed(true);
    lockViewportHeight();
    recoverViewportAfterKeyboard();
    setKeyboardOpen(true);
    scheduleOpenSyncBurst();
  }, [scheduleOpenSyncBurst, setKeyboardOpen]);

  const closeKeyboardLayout = useCallback(() => {
    inputFocusedRef.current = false;
    clearOpenSyncTimers();
    setKeyboardOpen(false);
    resetShellViewport();

    if (closeSettleTimerRef.current !== null) {
      window.clearTimeout(closeSettleTimerRef.current);
    }
    closeSettleTimerRef.current = window.setTimeout(() => {
      closeSettleTimerRef.current = null;
      resetShellViewport();
      setViewportFillSuppressed(false);
      unlockViewportHeight();
      scheduleViewportRecovery();
    }, CLOSE_SETTLE_MS);
  }, [clearOpenSyncTimers, resetShellViewport, setKeyboardOpen]);

  const cancelBlurDefer = useCallback(() => {
    if (blurDeferTimerRef.current !== null) {
      window.clearTimeout(blurDeferTimerRef.current);
      blurDeferTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const vv = window.visualViewport;
    const onViewportResize = () => {
      if (!inputFocusedRef.current) return;
      // 动画过程防抖，避免频繁改 height 把光标甩飞
      if (resizeDebounceTimerRef.current !== null) {
        window.clearTimeout(resizeDebounceTimerRef.current);
      }
      resizeDebounceTimerRef.current = window.setTimeout(() => {
        resizeDebounceTimerRef.current = null;
        syncShellToVisualViewport({ recoverScroll: false, nudgeCaret: true });
      }, 80);
    };
    const onViewportScroll = () => {
      if (!inputFocusedRef.current) return;
      // scroll 时只补高度（offsetTop 变化），不要 scrollTo 打架
      syncShellToVisualViewport({ recoverScroll: false, nudgeCaret: true });
    };
    vv?.addEventListener("resize", onViewportResize);
    vv?.addEventListener("scroll", onViewportScroll);
    return () => {
      cancelBlurDefer();
      clearOpenSyncTimers();
      if (closeSettleTimerRef.current !== null) {
        window.clearTimeout(closeSettleTimerRef.current);
        closeSettleTimerRef.current = null;
      }
      vv?.removeEventListener("resize", onViewportResize);
      vv?.removeEventListener("scroll", onViewportScroll);
      inputFocusedRef.current = false;
      setKeyboardOpen(false);
      resetShellViewport();
      setViewportFillSuppressed(false);
      unlockViewportHeight();
    };
  }, [cancelBlurDefer, clearOpenSyncTimers, resetShellViewport, setKeyboardOpen, syncShellToVisualViewport]);

  const onInputFocus = useCallback(() => {
    cancelBlurDefer();
    openKeyboardLayout();
  }, [cancelBlurDefer, openKeyboardLayout]);

  const onInputBlur = useCallback(() => {
    cancelBlurDefer();
    blurDeferTimerRef.current = window.setTimeout(() => {
      blurDeferTimerRef.current = null;
      closeKeyboardLayout();
    }, BLUR_DEFER_MS);
  }, [cancelBlurDefer, closeKeyboardLayout]);

  const onComposePointerDown = useCallback(() => {
    cancelBlurDefer();
  }, [cancelBlurDefer]);

  return { onInputFocus, onInputBlur, onComposePointerDown, closeKeyboardLayout };
}

export { HTML_KEYBOARD_CLASS as CS_KEYBOARD_OPEN_CLASS };
